import { Dispatch, SetStateAction, useState } from "react";
import {
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogActions,
} from "@material-ui/core";
import {
  FileCopy,
  Delete,
  ToggleOn,
  ToggleOff,
} from "@material-ui/icons";
import { MainTableCell, ButtonCustom, CancelButton } from "../../shared/Style/Style";
import {
  ShareLink,
  ShareLinkTypeEnum,
  useActivateShareLinkMutation,
  useDeactivateShareLinkMutation,
  useDeleteShareLinkMutation,
} from "../../../generated/graphql";
import { ShareLinksListItem, ShortCodeCell, ShareUrlCell, StatusChip } from "../styles";
import { formatDate } from "../utils";
import { initialNotifications } from "../ShareLinks";

interface ShareLinkRowProps {
  link: Partial<ShareLink>;
  canManage: boolean;
  onOpenModal: (id: string) => void;
  setNotifications: Dispatch<
    SetStateAction<{ error: boolean; success: boolean; message: string }>
  >;
}

export const ShareLinkRow = ({
  link,
  canManage,
  onOpenModal,
  setNotifications,
}: ShareLinkRowProps) => {
  const [confirmDelete, setConfirmDelete] = useState(false);

  const refetch = {
    refetchQueries: ["shareLinks"],
    awaitRefetchQueries: true,
  };
  const [activate] = useActivateShareLinkMutation(refetch);
  const [deactivate] = useDeactivateShareLinkMutation(refetch);
  const [deleteShareLink] = useDeleteShareLinkMutation(refetch);

  const notify = (success: boolean, message: string) =>
    setNotifications({ ...initialNotifications, [success ? "success" : "error"]: true, message });

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(link.shareUrl || "");
      notify(true, "Посилання скопійовано");
    } catch {
      notify(false, "Не вдалося скопіювати посилання");
    }
  };

  const handleToggle = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const action = link.isActive ? deactivate : activate;
      await action({ variables: { id: link.id as string } });
    } catch {
      notify(false, "Помилка при зміні статусу");
    }
  };

  const handleDelete = async () => {
    try {
      await deleteShareLink({ variables: { id: link.id as string } });
      notify(true, "Посилання видалено");
    } catch {
      notify(false, "Помилка при видаленні");
    } finally {
      setConfirmDelete(false);
    }
  };

  return (
    <>
      <ShareLinksListItem onClick={() => canManage && onOpenModal(link.id as string)}>
        <MainTableCell>
          <ShortCodeCell>{link.shortCode}</ShortCodeCell>
        </MainTableCell>
        <MainTableCell>
          <ShareUrlCell>
            <Tooltip title={link.shareUrl || ""} placement="top">
              <span>{link.shareUrl}</span>
            </Tooltip>
            <Tooltip title="Скопіювати">
              <IconButton size="small" onClick={handleCopy}>
                <FileCopy fontSize="small" />
              </IconButton>
            </Tooltip>
          </ShareUrlCell>
        </MainTableCell>
        <MainTableCell>
          {link.type === ShareLinkTypeEnum.ExternalUrl ? (
            <Tooltip title={link.externalUrl || ""} placement="top">
              <span>{link.externalUrl || "—"}</span>
            </Tooltip>
          ) : (
            link.targetUlc?.name || "—"
          )}
        </MainTableCell>
        <MainTableCell>
          <StatusChip active={!!link.isActive}>
            {link.isActive ? "Активне" : "Неактивне"}
          </StatusChip>
        </MainTableCell>
        <MainTableCell>{formatDate(link.expiresAt)}</MainTableCell>
        <MainTableCell>
          {canManage && (
            <>
              <Tooltip title={link.isActive ? "Деактивувати" : "Активувати"}>
                <IconButton size="small" onClick={handleToggle}>
                  {link.isActive ? (
                    <ToggleOn color="primary" />
                  ) : (
                    <ToggleOff color="disabled" />
                  )}
                </IconButton>
              </Tooltip>
              <Tooltip title="Видалити">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setConfirmDelete(true);
                  }}
                >
                  <Delete color="error" />
                </IconButton>
              </Tooltip>
            </>
          )}
        </MainTableCell>
      </ShareLinksListItem>

      <Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)}>
        <DialogTitle>Видалити посилання «{link.shortCode}»?</DialogTitle>
        <DialogActions>
          <CancelButton onClick={() => setConfirmDelete(false)}>
            Скасувати
          </CancelButton>
          <ButtonCustom onClick={handleDelete}>Видалити</ButtonCustom>
        </DialogActions>
      </Dialog>
    </>
  );
};
